/**
 * Store Pinia pour le module Semaine Promo
 */

import promoService from "@/services/promo.service";
import type {
  PromoBanner,
  PromoCampaign,
  PromoOffer,
  PromoSupplier,
} from "@/types/promo";
import { computed, ref } from "@vue/runtime-core";
import { defineStore } from "pinia";

export const usePromoStore = defineStore("promo", () => {
  // État
  const banners = ref<PromoBanner[]>([]);
  const campaign = ref<PromoCampaign | null>(null);
  const suppliers = ref<PromoSupplier[]>([]);
  const currentSupplier = ref<PromoSupplier | null>(null);
  const currentOffers = ref<PromoOffer[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters calculés
  const hasBanners = computed(() => banners.value.length > 0);
  const isCampaignActive = computed(
    () => campaign.value?.is_currently_active ?? false
  );
  const sortedSuppliers = computed(() =>
    [...suppliers.value].sort((a, b) => a.name.localeCompare(b.name))
  );

  // Actions

  /**
   * Charge les bandeaux actifs
   */
  async function fetchBanners() {
    try {
      loading.value = true;
      error.value = null;
      banners.value = await promoService.getActiveBanners();
    } catch (err: any) {
      error.value = err.response?.data?.message || "Erreur lors du chargement des bandeaux";
      console.error("Erreur fetchBanners:", err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Charge la campagne active
   */
  async function fetchCampaign() {
    try {
      loading.value = true;
      error.value = null;
      campaign.value = await promoService.getCampaign();
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erreur lors du chargement de la campagne";
      console.error("Erreur fetchCampaign:", err);
      campaign.value = null;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Charge la liste des fournisseurs participants
   * @param brandFilter - Filtre optionnel par marque
   */
  async function fetchSuppliers(brandFilter?: string) {
    try {
      loading.value = true;
      error.value = null;
      suppliers.value = await promoService.getSuppliers(brandFilter);
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erreur lors du chargement des fournisseurs";
      console.error("Erreur fetchSuppliers:", err);
      suppliers.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Charge le détail d'un fournisseur + ses offres
   */
  async function fetchSupplierDetail(supplierId: number, brandFilter?: string) {
    try {
      loading.value = true;
      error.value = null;
      const data = await promoService.getSupplierDetail(supplierId, brandFilter);
      currentSupplier.value = data.supplier;
      currentOffers.value = data.offers;
    } catch (err: any) {
      error.value =
        err.response?.data?.message || "Erreur lors du chargement du fournisseur";
      console.error("Erreur fetchSupplierDetail:", err);
      currentSupplier.value = null;
      currentOffers.value = [];
    } finally {
      loading.value = false;
    }
  }

  /**
   * Télécharge une offre
   */
  function downloadOffer(offerId: number) {
    promoService.downloadOffer(offerId);
  }

  /**
   * Récupère l'URL de visualisation d'une offre PDF
   */
  function getOfferViewUrl(offerId: number): string {
    return promoService.getOfferViewUrl(offerId);
  }

  /**
   * Réinitialise l'état du fournisseur courant
   */
  function clearCurrentSupplier() {
    currentSupplier.value = null;
    currentOffers.value = [];
  }

  /**
   * Réinitialise tout le store
   */
  function reset() {
    banners.value = [];
    campaign.value = null;
    suppliers.value = [];
    currentSupplier.value = null;
    currentOffers.value = [];
    loading.value = false;
    error.value = null;
  }

  return {
    // État
    banners,
    campaign,
    suppliers,
    currentSupplier,
    currentOffers,
    loading,
    error,

    // Getters
    hasBanners,
    isCampaignActive,
    sortedSuppliers,

    // Actions
    fetchBanners,
    fetchCampaign,
    fetchSuppliers,
    fetchSupplierDetail,
    downloadOffer,
    getOfferViewUrl,
    clearCurrentSupplier,
    reset,
  };
});
